/** XeAgents.ai **/
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Phone, PhoneForwarded } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface Callback {
  id: string;
  callId: string | null;
  patientName: string | null;
  phone: string | null;
  reason: string;
  priority: "normal" | "urgent";
  status: "open" | "transferring" | "in_progress" | "done" | "cancelled";
  assignedTo: string | null;
  notes: string | null;
  createdAt: string;
}

const STATUS_LABEL: Record<Callback["status"], string> = {
  open: "Needs a call back",
  transferring: "Being put through",
  in_progress: "In progress",
  done: "Done",
  cancelled: "Dismissed",
};

function ago(iso: string) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return new Date(iso).toLocaleDateString();
}

function Task({ task }: { task: Callback }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState(task.notes ?? "");
  const [owner, setOwner] = useState(task.assignedTo ?? "");
  const closed = task.status === "done" || task.status === "cancelled";

  const update = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => (await apiRequest("PATCH", `/api/healthcare/callbacks/${task.id}`, patch)).json(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/healthcare/callbacks"] }),
    onError: (e: any) => toast({ title: "Could not update the task", description: e.message, variant: "destructive" }),
  });

  return (
    <Card className={task.priority === "urgent" && !closed ? "border-destructive" : undefined} data-testid={`callback-${task.id}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              {task.priority === "urgent" && (
                <Badge variant="destructive">
                  <AlertTriangle className="mr-1 h-3 w-3" /> Urgent
                </Badge>
              )}
              <Badge variant={closed ? "outline" : "secondary"}>{STATUS_LABEL[task.status]}</Badge>
              <span className="text-xs text-muted-foreground">{ago(task.createdAt)}</span>
            </div>
            <p className="font-medium">{task.patientName || "Unknown caller"}</p>
            {task.phone && (
              <a href={`tel:${task.phone}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                <Phone className="h-3 w-3" /> {task.phone}
              </a>
            )}
          </div>
          {task.callId && (
            <Button asChild variant="ghost" size="sm">
              <a href={`/app/calls/${task.callId}`}>View call</a>
            </Button>
          )}
        </div>
        <p className="text-sm">{task.reason}</p>

        {!closed && (
          <div className="grid gap-2 md:grid-cols-[200px_1fr]">
            <Input value={owner} maxLength={120} placeholder="Assigned to" aria-label="Assigned to" onChange={(e) => setOwner(e.target.value)} onBlur={() => owner !== (task.assignedTo ?? "") && update.mutate({ assignedTo: owner })} />
            <Textarea value={notes} rows={1} maxLength={2000} placeholder="Notes for the team" aria-label="Notes" onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (task.notes ?? "") && update.mutate({ notes })} />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {!closed && task.status !== "in_progress" && (
            <Button size="sm" variant="outline" onClick={() => update.mutate({ status: "in_progress" })} disabled={update.isPending}>
              <PhoneForwarded className="mr-1 h-4 w-4" /> Start
            </Button>
          )}
          {!closed && (
            <Button size="sm" onClick={() => update.mutate({ status: "done" })} disabled={update.isPending} data-testid={`button-done-${task.id}`}>
              <Check className="mr-1 h-4 w-4" /> Mark done
            </Button>
          )}
          {!closed && (
            <Button size="sm" variant="ghost" onClick={() => update.mutate({ status: "cancelled" })} disabled={update.isPending}>
              Dismiss
            </Button>
          )}
          {closed && (
            <Button size="sm" variant="outline" onClick={() => update.mutate({ status: "open" })} disabled={update.isPending}>
              Reopen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CallbackQueue() {
  const [showClosed, setShowClosed] = useState(false);
  const { data: tasks = [], isLoading } = useQuery<Callback[]>({
    queryKey: [`/api/healthcare/callbacks${showClosed ? "?closed=1" : ""}`],
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Callback queue</h3>
          <p className="text-sm text-muted-foreground">Everything the receptionist could not finish ends up here with the reason, so no call is lost. Urgent items come first.</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={showClosed} onCheckedChange={setShowClosed} /> Show finished
        </label>
      </div>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && tasks.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">Nothing waiting. New messages and missed hand-overs will appear here.</CardContent>
        </Card>
      )}
      <div className="space-y-3">
        {tasks.map((t) => (
          <Task key={t.id} task={t} />
        ))}
      </div>
    </div>
  );
}
